import { Check, Zap } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useTierStore } from '@/stores/tier-store'
import { usePlanConstraint } from '@/context/plan-constraint-hooks'
import { ANSWER_TIER_META, allowedTiers, clampTierToPlan } from '@/features/companion/answer-tiers'
import { PLAN_LABELS } from '@/context/plan-constraint-utils'

const EFFORT_HINTS: Record<string, string> = {
  fast: 'Quick answers, lowest latency',
  normal: 'Balanced quality and cost',
  heavy: 'Deep multi-step analysis',
}

/**
 * The effort chip in the composer's controls row: `⚡ Fast|Normal|Heavy`,
 * opening a dropdown of the answer tiers with per-tier hints and the same
 * plan gating the old ChatControls/CompanionOptionsMenu applied.
 */
export function CompanionEffortChip() {
  const storedTier = useTierStore((s) => s.tier)
  const setTier = useTierStore((s) => s.setTier)
  const { planTier } = usePlanConstraint()

  const tiers = allowedTiers(planTier)
  // Effective tier: clamp stored preference to what the plan allows.
  const effectiveTier = clampTierToPlan(storedTier, planTier)
  const effortLabel = ANSWER_TIER_META.find((t) => t.id === effectiveTier)?.label ?? 'Normal'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Answer effort"
          aria-label="Effort"
          className="flex h-6 shrink-0 items-center gap-1 rounded-full px-2 text-xs text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
        >
          <Zap size={11} />
          {effortLabel}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="z-[80] w-64">
        <p className="px-2 pb-1.5 pt-1 text-[10px] leading-snug text-muted-foreground/70">
          Higher effort means more thorough answers, but takes longer and uses your credits faster.
        </p>
        {ANSWER_TIER_META.map((t) => {
          const allowed = tiers.includes(t.id)
          return (
            <DropdownMenuItem
              key={t.id}
              disabled={!allowed}
              onSelect={() => setTier(t.id)}
              aria-label={t.label}
              className="flex items-start gap-2 text-xs"
            >
              <span className="flex-1">
                <span className={cn('block', !allowed && 'text-muted-foreground/50')}>
                  {t.label}
                </span>
                <span className="block text-[10px] text-muted-foreground/60">
                  {allowed ? EFFORT_HINTS[t.id] : `Requires ${PLAN_LABELS[t.minPlan]}`}
                </span>
              </span>
              {effectiveTier === t.id && (
                <Check size={13} className="mt-0.5 shrink-0 text-primary" />
              )}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
